'use client';

import {
  PhoneNumberFormat,
  PhoneNumberType,
  PhoneNumberUtil,
} from 'google-libphonenumber';

const phoneUtil = PhoneNumberUtil.getInstance();

/**
 * Formats a phone number as the user types (as-you-type formatting)
 * Returns the formatted phone number without the country code prefix
 * Always formats progressively, even for incomplete numbers
 *
 * @param phone - The phone number digits (may contain formatting)
 * @param countryCode - ISO country code (e.g., "US")
 * @returns Formatted phone number string
 */
export function formatPhoneNumberAsYouType(
  phone: string,
  countryCode: string,
): string {
  if (!phone || !countryCode) {
    return phone;
  }

  // Extract only digits
  const digitsOnly = phone.replace(/\D/g, '');

  if (!digitsOnly) {
    return '';
  }

  try {
    // Get example number to understand the format pattern
    const exampleNumber =
      phoneUtil.getExampleNumberForType(countryCode, PhoneNumberType.MOBILE) ||
      phoneUtil.getExampleNumber(countryCode);

    if (!exampleNumber) {
      // Fallback: return digits with basic formatting
      return formatBasicPhoneNumber(digitsOnly, countryCode);
    }

    // Format the example number to get the pattern
    const exampleFormatted = phoneUtil.format(
      exampleNumber,
      PhoneNumberFormat.NATIONAL,
    );

    // Extract the pattern from the example (replace digits with placeholders)
    const pattern = exampleFormatted.replace(/\d/g, '9');

    // Try to parse and format the actual number
    try {
      const dialCode = `+${exampleNumber.getCountryCode()}`;
      const fullNumber = dialCode + digitsOnly;
      const parsed = phoneUtil.parse(fullNumber, countryCode);
      const formatted = phoneUtil.format(parsed, PhoneNumberFormat.NATIONAL);
      return formatted;
    } catch {
      // If parsing fails (number too short/incomplete), format using pattern
      return formatUsingPattern(digitsOnly, pattern, countryCode);
    }
  } catch (e) {
    console.error('Error formatting phone number:', e);
    // Fallback to basic formatting
    return formatBasicPhoneNumber(digitsOnly, countryCode);
  }
}

/**
 * Formats digits using a pattern template (from example number)
 * Applies formatting progressively as digits are entered
 * Example: pattern "(999) 999-9999", digits "1234567" -> "(123) 456-7"
 */
function formatUsingPattern(
  digits: string,
  pattern: string,
  _countryCode: string,
): string {
  if (!digits || !pattern) {
    return digits;
  }

  // Build formatted string by inserting digits into pattern positions
  let formatted = '';
  let digitIndex = 0;

  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === '9') {
      // This is a digit position
      if (digitIndex < digits.length) {
        formatted += digits[digitIndex];
        digitIndex++;
      } else {
        // No more digits, stop formatting
        break;
      }
    } else {
      // This is a formatting character
      // Only add it if we have digits filled up to this point
      // Count how many '9's come before this position
      let digitsBeforeThis = 0;
      for (let j = 0; j < i; j++) {
        if (pattern[j] === '9') {
          digitsBeforeThis++;
        }
      }

      // Add formatting character if we have enough digits filled
      if (digitIndex >= digitsBeforeThis) {
        formatted += pattern[i];
      } else {
        // Not enough digits yet, stop here
        break;
      }
    }
  }

  return formatted;
}

/**
 * Basic phone number formatting for common patterns
 * Used as fallback when google-libphonenumber can't format incomplete numbers
 * Formats progressively as digits are entered
 */
function formatBasicPhoneNumber(digits: string, countryCode: string): string {
  if (!digits) return '';

  // US/Canada format: (XXX) XXX-XXXX
  if (countryCode === 'US' || countryCode === 'CA') {
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  }

  // UK format: XXXX XXX XXXX
  if (countryCode === 'GB') {
    if (digits.length <= 4) {
      return digits;
    } else if (digits.length <= 7) {
      return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    } else {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
    }
  }

  // France format: X XX XX XX XX
  if (countryCode === 'FR') {
    if (digits.length <= 1) {
      return digits;
    } else if (digits.length <= 3) {
      return `${digits.slice(0, 1)} ${digits.slice(1)}`;
    } else if (digits.length <= 5) {
      return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3)}`;
    } else if (digits.length <= 7) {
      return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5)}`;
    } else {
      return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`;
    }
  }

  // Germany format: XXXX XXXXXXX
  if (countryCode === 'DE') {
    if (digits.length <= 4) {
      return digits;
    } else {
      return `${digits.slice(0, 4)} ${digits.slice(4, 11)}`;
    }
  }

  // Italy format: XXX XXXX XXXX
  if (countryCode === 'IT') {
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 7) {
      return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7, 11)}`;
    }
  }

  // Spain format: XXX XXX XXX
  if (countryCode === 'ES') {
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`;
    }
  }

  // Brazil format: (XX) XXXXX-XXXX
  if (countryCode === 'BR') {
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 7) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    } else {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
    }
  }

  // Japan format: XX-XXXX-XXXX
  if (countryCode === 'JP') {
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    } else {
      return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
    }
  }

  // Australia format: XXXX XXX XXX
  if (countryCode === 'AU') {
    if (digits.length <= 4) {
      return digits;
    } else if (digits.length <= 7) {
      return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    } else {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
    }
  }

  // Default: group digits in chunks of 3-4
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  } else if (digits.length <= 9) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  } else {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
  }
}

/**
 * Formats a complete phone number using google-libphonenumber
 * Returns the formatted phone number in NATIONAL format
 *
 * @param phone - The phone number digits (without country code)
 * @param countryCode - ISO country code (e.g., "US")
 * @returns Formatted phone number string
 */
export function formatPhoneNumber(phone: string, countryCode: string): string {
  return formatPhoneNumberAsYouType(phone, countryCode);
}

/**
 * Generates a valid E.164 example mobile phone number for the given
 * country, used as a placeholder when the backend requires a phone
 * field but the user signed up via email (no real number to send).
 *
 * Mirrors RN `usePopStoreAccountForm.generateExamplePhone`.
 *
 * @param countryCode - ISO country code (e.g., "US"). Defaults to "US".
 * @returns Example phone number in E.164 (e.g., "+12025550123"), or
 *          empty string if the lookup fails.
 */
export function generateExamplePhone(countryCode: string = 'US'): string {
  try {
    const example = phoneUtil.getExampleNumberForType(
      countryCode,
      PhoneNumberType.MOBILE,
    );
    return phoneUtil.format(example, PhoneNumberFormat.E164);
  } catch {
    return '';
  }
}

/**
 * Extracts only digits from a phone number string
 *
 * @param phone - Phone number string (may contain formatting)
 * @returns Digits only string
 */
export function getPhoneDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

/**
 * Formats a stored phone number for display by auto-detecting the country
 * from the "+" prefix. Numbers with a "+" prefix are parsed internationally
 * and formatted in their national format (e.g. "+12015550123" → "(201) 555-0123",
 * "+447911123456" → "07911 123456"). Numbers without a prefix default to US.
 * Falls back to the raw value if parsing fails.
 */
export function formatDisplayPhone(phone: string): string {
  try {
    const parsed = phone.startsWith('+')
      ? phoneUtil.parse(phone)
      : phoneUtil.parse(phone, 'US');
    return phoneUtil.format(parsed, PhoneNumberFormat.INTERNATIONAL);
  } catch {
    return phone;
  }
}

/**
 * Format a national phone number into its E.164 form (e.g.
 * `"5551234567"` + `"US"` → `"+15551234567"`). Used at save time when
 * persisting to `stores.contactCard.phone` — RN's contact-card editor
 * stores phone as a single E.164 string (no separate country field),
 * so popstore-next must save in the same shape for the two views to
 * round-trip. Returns `undefined` when the inputs can't be parsed.
 */
export function formatPhoneE164(
  national: string | null | undefined,
  countryCode: string | null | undefined,
): string | undefined {
  if (!national) return undefined;
  const digits = national.replace(/\D/g, '');
  if (!digits) return undefined;
  const region = (countryCode || 'US').toUpperCase();
  try {
    const parsed = phoneUtil.parse(digits, region);
    return phoneUtil.format(parsed, PhoneNumberFormat.E164);
  } catch {
    return undefined;
  }
}

/**
 * Parse an E.164 phone (e.g. `+12223338919`) into its national digits
 * and ISO region code. Returns `undefined` when parsing fails so the
 * caller can skip the pre-fill rather than render garbage.
 */
export function parseE164Phone(
  phone: string | null | undefined,
): { nationalNumber: string; countryCode: string } | undefined {
  if (!phone) return undefined;
  try {
    const parsed = phoneUtil.parse(phone);
    // `getRegionCodeForNumber` is strict — it returns "" for numbers
    // whose national portion isn't a known assignment (e.g. test
    // numbers like `+12223338919` where `222` isn't a valid NANP area
    // code). Fall back to the dial-code → main-region lookup so any
    // E.164-formatted input at least gets a usable country.
    const regionFromNumber = phoneUtil.getRegionCodeForNumber(parsed);
    const dialCode = parsed.getCountryCode();
    const regionFromDialCode = dialCode
      ? phoneUtil.getRegionCodeForCountryCode(dialCode)
      : '';
    const countryCode = regionFromNumber || regionFromDialCode;
    if (!countryCode) return undefined;
    return {
      nationalNumber: String(parsed.getNationalNumber()),
      countryCode,
    };
  } catch {
    return undefined;
  }
}
